import { useEffect, useState } from "react";

import { getFromLocalStorage, saveToLocalStorage } from "utils/localStorage";
import { getSessionFromStorage } from "utils/sessionStorageHandler";
import { encodeEmail } from "utils/common/common";

import { TRAINING_TAB_KEY } from "const";

interface ITrainingTab {
  tabId: string;
  sessionId: string;
}

// AIDEV-NOTE: Identita záložky a to, které tréninky už zabrala nebo jí je jiná záložka vzala,
// jsou na úrovni modulu, tedy jednou na načtení stránky. Trasa `/cviceni/*` má v userRouter.tsx
// `key={Math.random()}` a stránku tréninku při překreslení routeru znovu připojí — stav
// v komponentě by záložce dal nové id a znovupřipojení by zámek převzalo zpátky. Zabírá se
// proto jen jednou na trénink a odemknout jde jen znovunačtením stránky. [TD-461]
const TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const claimedTrainings = new Set<string>();
const lockedTrainings = new Set<string>();

/**
 * Trénink smí běžet jen v jedné záložce. Záložka, která trénink otevře, se zapíše jako
 * vlastník a ostatní záložky se stejným tréninkem se zamknou — jinak by si každá vedla
 * vlastní výsledky, odpovídalo by se dvakrát a dokončení v jedné by druhou nechalo
 * odpovídat do zavřeného tréninku. Stav tréninku je v localStorage sdílený, takže nová
 * záložka naváže tam, kde stará skončila. Jiné zařízení tohle nepozná, to musí hlídat
 * server. [TD-461]
 */
export const useTrainingTabLock = (sessionId: string | undefined, userEmail: string | null | undefined) => {
  const training = `${userEmail}|${sessionId}`;
  const [isLocked, setLocked] = useState(() => lockedTrainings.has(training));

  useEffect(() => {
    if (typeof sessionId !== "string" || typeof userEmail !== "string") return;

    const key = encodeEmail(TRAINING_TAB_KEY, userEmail);

    // Jen stránka s platným uloženým tréninkem — stará adresa dokončeného tréninku by jinak
    // zamkla záložku, která v něm ukazuje výsledky, a Exercise by ji vzápětí přesměroval pryč.
    if (!claimedTrainings.has(training) && getSessionFromStorage(userEmail)?.sessionId === sessionId) {
      claimedTrainings.add(training);
      saveToLocalStorage(key, { tabId: TAB_ID, sessionId } satisfies ITrainingTab);
    }

    // Jen stejný trénink: záložka se souhrnem dokončeného tréninku se nezamkne, když jinde
    // začne nový.
    const checkOwner = () => {
      const owner = getFromLocalStorage<ITrainingTab>(key);
      if (owner?.sessionId !== sessionId || owner.tabId === TAB_ID) return;

      lockedTrainings.add(training);
      setLocked(true);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === key) checkOwner();
    };

    // Znovupřipojená záložka mohla převzetí propásnout, kontroluje se proto hned.
    checkOwner();

    // AIDEV-NOTE: Uspaná záložka (mobil, pozadí) událost `storage` propásne — proto
    // kontrola i při každém znovuzobrazení, než student stihne odpovědět.
    window.addEventListener("storage", handleStorage);
    document.addEventListener("visibilitychange", checkOwner);

    return () => {
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", checkOwner);
    };
  }, [training, sessionId, userEmail]);

  return isLocked;
};
